import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { callAjaxIO } from 'Shared/ajax_io';
import type { ModerationPageData, ModerationView } from './moderation_types';

// The server-rendered page ages from page load, not from whenever the cache
// entry is (re)created — without this, a garbage-collected entry reseeded
// from props would present a stale tally as fresh.
const pageLoadedAt = Date.now();

export function moderationPageKey(view: ModerationView) {
   return ['moderation', view.queue, view.page, view.filter, view.voteid] as const;
}

/**
 * One page of one queue. The server renders the arriving page into props,
 * so only deeper pages and the unvoted filter fetch; keepPreviousData
 * leaves the current cards up while the next page loads.
 *
 * The staleTime is short on purpose: a tally that moved while you were
 * reading changes whether your vote decides the outcome, so coming back to
 * the tab should show it. It only has to outlast the mount, so the page
 * the server just rendered isn't fetched again on arrival.
 */
export function useModerationPage(view: ModerationView, initial?: ModerationPageData) {
   return useQuery({
      queryKey: moderationPageKey(view),
      queryFn: () => callAjaxIO<ModerationPageData>('getQueuePage', view),
      initialData: initial,
      initialDataUpdatedAt: pageLoadedAt,
      placeholderData: keepPreviousData,
      staleTime: 30_000,
   });
}

/** What became of the vote, as the page's handlers phrase it. 'closed'
 *  means somebody else decided it first, which is a race between two
 *  moderators rather than a failure. */
export interface VoteOutcome {
   outcome: 'recorded' | 'passed' | 'failed' | 'canceled' | 'closed';
}

/**
 * Casting and withdrawing a vote, against the page currently on screen.
 *
 * Both move the tally the moment they are clicked and put it back if the
 * server refuses, then refetch: passing a vote carries out what it
 * proposed and takes it out of the pending queue, and withdrawing one can
 * hand the row's identity to another voter, so the page has to be read
 * again either way.
 */
export function useVoteActions(queryKey: ReturnType<typeof moderationPageKey>) {
   const client = useQueryClient();

   const optimistic = async (voteid: number, myVote: number | null) => {
      await client.cancelQueries({ queryKey });
      const previous = client.getQueryData<ModerationPageData>(queryKey);
      if (previous) {
         client.setQueryData(queryKey, patchVote(previous, voteid, myVote));
      }
      return { previous };
   };

   const restore = (context: { previous?: ModerationPageData } | undefined) => {
      if (context?.previous) {
         client.setQueryData(queryKey, context.previous);
      }
   };

   const settle = () => {
      void client.invalidateQueries({ queryKey });
   };

   const castVote = useMutation({
      mutationFn: ({
         voteid,
         score,
         adminForce,
      }: {
         voteid: number;
         score: number;
         adminForce: boolean;
      }) => callAjaxIO<VoteOutcome>('vote', voteid, score, adminForce),
      onMutate: ({ voteid, score }) => optimistic(voteid, score),
      onError: (_error, _variables, context) => restore(context),
      onSettled: settle,
   });

   const cancelVote = useMutation({
      mutationFn: ({ voteid }: { voteid: number }) => callAjaxIO<VoteOutcome>('cancelVote', voteid),
      onMutate: ({ voteid }) => optimistic(voteid, null),
      onError: (_error, _variables, context) => restore(context),
      onSettled: settle,
   });

   return { castVote, cancelVote };
}

/**
 * The tally is a net score, so switching or withdrawing a vote moves it by
 * the difference, not by one. The voter chips only lose a row on a
 * withdrawal: while a vote stands, the reader's own chip is drawn from
 * myVote instead.
 */
function patchVote(
   page: ModerationPageData,
   voteid: number,
   myVote: number | null
): ModerationPageData {
   return {
      ...page,
      entries: page.entries.map(entry => {
         if (entry.voteid !== voteid) {
            return entry;
         }

         return {
            ...entry,
            myVote,
            score: entry.score - (entry.myVote ?? 0) + (myVote ?? 0),
            voters:
               myVote === null
                  ? entry.voters.filter(voter => voter.userid !== page.viewerid)
                  : entry.voters,
         };
      }),
   };
}
